import { useState, useEffect } from "react"
import { Autocomplete } from "@material-ui/lab"
import Button from "../../../components/Button"
import { Place } from "../../../types/global"
import { TextField } from "@material-ui/core"
import { Fragment } from "react"
import * as S from "../styles"
import { type MapyApiResponse } from "../../../types/global"

const MAPYCZ_API_KEY = "RD5Am5LzqmSacYx8a9QRgVVvtWNbMLgdRPD1Y6veDTY"

type AutocompleteOptions = {
  address: string
  latitude: number
  longitude: number
}

interface AddressInputProps {
  currentPlace: Place
  setCurrentPlace: Function
}

export default function AddressInput({
  currentPlace,
  setCurrentPlace,
}: AddressInputProps) {
  const [chnagingAddress, setChangingAddress] = useState(false)
  const [autocompleteOpen, setAutocompleteOpen] = useState(false)
  const [autocompleteOptions, setAutocompleteOptions] = useState<
    AutocompleteOptions[]
  >([])
  const [autocompleteValue, setAutocompleteValue] =
    useState<AutocompleteOptions | null>(null)
  const [autocompleteInputValue, setAutocompleteInputValue] = useState("")

  useEffect(() => {
    // When creating additional place for current user, reset autocomplete
    if (!currentPlace.id) {
      setAutocompleteValue(null)
      setAutocompleteOptions([])
      setAutocompleteInputValue("")
    }
  }, [currentPlace.id])

  // Autocomplete address
  useEffect(() => {
    let active = true
    let timer: number

    const clearTimer = () => {
      if (timer) {
        clearTimeout(timer)
      }
    }

    // Start search after typing at least 3 characters
    if (autocompleteInputValue.length < 3) {
      return undefined
    }

    /* "https://api.mapy.cz/suggest/?count=5&bounds=48.5370786,12.0921668|51.0746358,18.8927040&category=address_cz,street_cz,municipality_cz,ward_cz&phrase=" */
    ;(async () => {
      clearTimer()
      timer = window.setTimeout(async () => {
        const init = {
          headers: { "X-Mapy-Api-Key": MAPYCZ_API_KEY },
        }

        const response = await fetch(
          "https://api.mapy.cz/v1/suggest/?limit=5&locality=cz&type=regional.address&query=" +
            autocompleteInputValue.trim(),
          init,
        )

        const responseJson = (await response.json()) as MapyApiResponse

        const results = responseJson.items.map((res) => ({
          address: res.name + ", " + res.location,
          latitude: res.position.lat,
          longitude: res.position.lon,
        }))

        if (active) {
          setAutocompleteOptions(results)
        }
      }, 500)
    })()

    return () => {
      active = false
      clearTimer()
    }
  }, [autocompleteInputValue])

  const handleChangingAddress = () => {
    setAutocompleteValue(null)
    setCurrentPlace({...currentPlace, address: []})
    setChangingAddress(true)
  }

  useEffect(() => {
    if (autocompleteValue) {
      setCurrentPlace({
        ...currentPlace,
        address: [
          autocompleteValue?.address,
          0, // Place id for backward compatibility
          autocompleteValue?.latitude,
          autocompleteValue?.longitude,
        ],
      })
      setChangingAddress(false)
    }
  }, [autocompleteValue])

  return (
    <S.TextFieldWrapper>
      {currentPlace.address.length && !chnagingAddress ? (
        <div>
          {currentPlace.address[0]}
          <Button onClick={handleChangingAddress} color="white" blank>
            {`  ✕`}
          </Button>
        </div>
      ) : (
        <Autocomplete
          open={autocompleteOpen}
          onOpen={() => {
            setAutocompleteOpen(true)
          }}
          onClose={() => {
            setAutocompleteOpen(false)
          }}
          getOptionSelected={(option, value) =>
            option.address === value.address
          }
          filterOptions={(x) => x}
          options={autocompleteOptions}
          value={autocompleteValue}
          onChange={(_, address) => setAutocompleteValue(address)}
          noOptionsText="Začni ulicí a číslem ..."
          inputValue={autocompleteInputValue}
          onInputChange={(_, newValue) =>
            setAutocompleteInputValue(newValue)
          }
          getOptionLabel={(option) => option.address || ""}
          renderOption={(option) => option.address || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Adresa *"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <Fragment>{params.InputProps.endAdornment}</Fragment>
                ),
              }}
            />
          )}
        />
      )}
    </S.TextFieldWrapper>
  )
}
